import { useContext } from "react";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { CategoryType, BreadBudget } from "breadcommon";
import { DollarAmount } from "../common/DollarAmount";
import { buildClasses } from "../utils/buildClasses";
import { Dayjs } from "dayjs";

export function CategoryTypeTotals(props: {
  beginDate: Dayjs;
  endDate: Dayjs;
  budget?: BreadBudget | null;
}) {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const preferences = useContext(PreferencesContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const defaultCurrency = preferences?.web?.default_currency ?? "USD";
  const targetCurrency = props.budget?.currency ?? defaultCurrency;

  const convertedTransactions = convertTransactionsToSpecificCurrency(
    Array.from(transactions.values()),
    targetCurrency,
  );
  const filteredTransactions = convertedTransactions.filter(
    (transaction) =>
      transaction.reviewed &&
      transaction.date >= props.beginDate &&
      transaction.date <= props.endDate,
  );

  let incomeTotal = 0;
  let expenseTotal = 0;
  let savingsTotal = 0;

  filteredTransactions.forEach((transaction) => {
    if (!transaction.categoryId) return;

    const category = categories.get(transaction.categoryId);
    if (!category) return;

    switch (category.type) {
      case CategoryType.Income:
        incomeTotal += transaction.amount;
        break;
      case CategoryType.Expense:
        expenseTotal += transaction.amount;
        break;
      case CategoryType.Savings:
        savingsTotal += transaction.amount;
        break;
    }
  });

  // Calculate budgeted totals by category type
  let budgetedIncomeTotal = 0;
  let budgetedExpenseTotal = 0;
  let budgetedSavingsTotal = 0;

  if (props.budget) {
    props.budget.line_items.forEach((lineItem) => {
      const category = categories.get(lineItem.category_id);
      if (!category) return;

      switch (category.type) {
        case CategoryType.Income:
          budgetedIncomeTotal += lineItem.amount;
          break;
        case CategoryType.Expense:
          budgetedExpenseTotal += lineItem.amount;
          break;
        case CategoryType.Savings:
          budgetedSavingsTotal += lineItem.amount;
          break;
      }
    });
  }

  // Each total in its natural direction (earned, spent, saved), so it shows
  // as a plain amount and only a contrary one, like net refunds, gets a sign.
  const totals: {
    label: string;
    amount: number;
    budgeted: number | null;
    color: "green" | "blue" | "purple";
    isLimit?: boolean;
  }[] = [
    {
      label: "Income",
      amount: incomeTotal,
      budgeted: props.budget ? budgetedIncomeTotal : null,
      color: "green",
    },
    {
      label: "Expenses",
      amount: -expenseTotal,
      budgeted: props.budget ? budgetedExpenseTotal : null,
      color: "blue",
      isLimit: true,
    },
    {
      label: "Savings",
      amount: savingsTotal,
      budgeted: props.budget ? budgetedSavingsTotal : null,
      color: "purple",
    },
  ];

  return (
    <div
      className={buildClasses(
        "grid",
        "grid-cols-3",
        "gap-8",
        "max-w-3xl",
        "mb-2",
      )}
    >
      {totals.map((total) => (
        <CategoryTypeTotal
          key={total.label}
          {...total}
          currencyCode={targetCurrency}
        />
      ))}
    </div>
  );
}

/**
 * One total against its budget: the amount, what was budgeted, and a thin bar
 * of how much of the budget it's reached, in the chart's colors for its type.
 * A limit, like expenses, shows its amount in red once it's past the budget.
 */
function CategoryTypeTotal({
  label,
  amount,
  budgeted,
  color,
  isLimit = false,
  currencyCode,
}: {
  label: string;
  amount: number;
  budgeted: number | null;
  color: "green" | "blue" | "purple";
  isLimit?: boolean;
  currencyCode: string;
}): JSX.Element {
  const hasTarget = budgeted !== null && budgeted !== 0;
  const overLimit = isLimit && hasTarget && amount > budgeted;
  const reached = hasTarget ? Math.min(Math.max(amount / budgeted, 0), 1) : 0;

  return (
    <div className={buildClasses("flex", "flex-col", "gap-1.5", "min-w-0")}>
      <span className={buildClasses("text-sm", "text-on-surface-500")}>
        {label}
      </span>
      <div
        className={buildClasses(
          "flex",
          "items-baseline",
          "gap-1.5",
          "whitespace-nowrap",
        )}
      >
        <span
          className={buildClasses("font-medium", {
            if: overLimit,
            then: "text-red-600",
            else: "text-on-surface-800",
          })}
        >
          <DollarAmount
            n={amount}
            size="lg"
            showDollarSign={true}
            showMinus={true}
            monospaceFont={false}
            align="span"
            currencyCode={currencyCode}
          />
        </span>
        {budgeted !== null && (
          <span className={buildClasses("text-sm", "text-on-surface-500")}>
            {hasTarget ? (
              <>
                of{" "}
                <DollarAmount
                  n={budgeted}
                  size="sm"
                  showDollarSign={true}
                  monospaceFont={false}
                  align="span"
                  currencyCode={currencyCode}
                />
              </>
            ) : (
              "no target"
            )}
          </span>
        )}
      </div>
      <div
        className={buildClasses("h-1.5", "rounded-full", "overflow-hidden", {
          switch: hasTarget ? color : "none",
          cases: new Map([
            ["green", "bg-green-200"],
            ["blue", "bg-blue-200"],
            ["purple", "bg-purple-200"],
            ["none", "bg-surface-500"],
          ]),
        })}
      >
        <div
          className={buildClasses("h-full", "rounded-full", {
            switch: color,
            cases: new Map([
              ["green", "bg-green-500"],
              ["blue", "bg-blue-500"],
              ["purple", "bg-purple-500"],
            ]),
          })}
          style={{ width: `${reached * 100}%` }}
        />
      </div>
    </div>
  );
}
